import * as React from 'react';
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { 
  Box, Paper, IconButton, TextField, Modal,
  Table, TableHead, TableBody, TableCell, TableContainer, 
  TableFooter, TablePagination, TableRow 
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';
import moment from 'moment';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PulseLoader from 'react-spinners/PulseLoader';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import ClearIcon from '@mui/icons-material/Clear';

// API
import { useGetOfficeItemsQuery } from '../../../features/officeitems/officeItemsApiSlice';

// Styles
import { TableContStyle, TableHeadStyle } from '../../../Styles/TableStyles';
import { HeadingWrap, Span, CommonButton } from '../../../Styles/CommonStyles';
import ExcelDownload from '../../../data/exceldownload';

// Extend dayjs with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const Button2Div = styled.div`
  display: flex;
  justify-content: end;
`;

const Wrapper = styled.div`
  display: flex;
  background-color: white;
  align-items: center;
`;

const RangeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const tableFontSize = 15;

// Styled component for consumables breakdown cell with ellipsis
const ConsumablesBreakdownCell = styled.div`
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
`;

// Helper function to generate consumables breakdown string
const getConsumablesBreakdown = (consumable_details) => {
  if (!consumable_details || consumable_details.length === 0) return '-';
  
  // Group by consumable name and sum quantities
  const consumableMap = {};
  consumable_details.forEach(item => {
    const name = item.consumable || 'Unknown';
    if (consumableMap[name]) {
      consumableMap[name] += item.quantity || 0;
    } else {
      consumableMap[name] = item.quantity || 0;
    }
  });
  
  // Format as "Name (qty), Name2 (qty2), ..."
  return Object.entries(consumableMap)
    .map(([name, qty]) => `${name} (${qty})`)
    .join(', ');
};

// Modal Component for Viewing Consumable Details
const ConsumableDetailsModal = ({ open, onClose, officeData }) => {
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(10);
  const [detailSearchData, setDetailSearchData] = useState({
    partno: '',
    consumable: '',
  });

  const handleDetailChangePage = (event, newPage) => {
    setDetailPage(newPage);
  };

  const handleDetailChangeRowsPerPage = (event) => {
    setDetailRowsPerPage(parseInt(event.target.value, 10));
    setDetailPage(0);
  };

  const handleDetailSearchChange = (event) => {
    setDetailSearchData({
      ...detailSearchData,
      [event.target.name]: event.target.value,
    });
    setDetailPage(0);
  };

  // Filter consumable details based on search
  const filteredDetails = useMemo(() => {
    if (!officeData?.consumable_details) return [];
    
    let filtered = [...officeData.consumable_details];
    
    if (detailSearchData.partno) {
      filtered = filtered.filter(item => 
        item.partno?.toLowerCase().includes(detailSearchData.partno.toLowerCase())
      );
    }
    
    if (detailSearchData.consumable) {
      filtered = filtered.filter(item => 
        item.consumable?.toLowerCase().includes(detailSearchData.consumable.toLowerCase())
      );
    }
    
    return filtered;
  }, [officeData, detailSearchData]);

  // Prepare download data for the modal (after filteredDetails is defined)
  const modalDownloadData = useMemo(() => {
    if (!filteredDetails || filteredDetails.length === 0) return [];
    
    return filteredDetails.map((item, index) => ({
      'Sl.No.': index + 1,
      'Part No.': item.partno || '-',
      'Consumable': item.consumable || '-',
      'Quantity': item.quantity || 0,
      'Last Updated': item.last_updated 
        ? moment(item.last_updated).format('DD-MM-YYYY HH:mm')
        : '-'
    }));
  }, [filteredDetails]);

  const emptyRows = detailRowsPerPage - Math.min(detailRowsPerPage, filteredDetails.length - detailPage * detailRowsPerPage);

  if (!officeData) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={ModalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <ModalTitle>
            Consumable Details - {officeData.office_name}
          </ModalTitle>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {modalDownloadData && modalDownloadData.length > 0 && (
              <ExcelDownload 
                csvData={modalDownloadData} 
                fileName={`${officeData.office_name}_Consumables_Details`}
              />
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ backgroundColor: TableHeadStyle.backgroundColor }}>
              <TableRow>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Sl.No.
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Part No.
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Consumable
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Quantity
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Last Updated
                </TableCell>
              </TableRow>

              {/* Search Row */}
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: 'white' }}
                    name="partno"
                    onChange={handleDetailSearchChange}
                    label="Search Part No"
                    size="small"
                    variant="outlined"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: 'white' }}
                    name="consumable"
                    onChange={handleDetailSearchChange}
                    label="Search Consumable"
                    size="small"
                    variant="outlined"
                    fullWidth
                  />
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(detailRowsPerPage > 0
                ? filteredDetails.slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage)
                : filteredDetails
              ).map((item, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontSize: tableFontSize }}>{detailPage * detailRowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontSize: tableFontSize }}>{item.partno || '-'}</TableCell>
                  <TableCell sx={{ fontSize: tableFontSize }}>{item.consumable || '-'}</TableCell>
                  <TableCell sx={{ fontSize: tableFontSize }}>{item.quantity || 0}</TableCell>
                  <TableCell sx={{ fontSize: tableFontSize }}>
                    {item.last_updated 
                      ? moment(item.last_updated).format('DD-MM-YYYY HH:mm')
                      : '-'
                    }
                  </TableCell>
                </TableRow>
              ))}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={5} />
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={5}
                  count={filteredDetails.length}
                  rowsPerPage={detailRowsPerPage}
                  page={detailPage}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleDetailChangePage}
                  onRowsPerPageChange={handleDetailChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    </Modal>
  );
};

// Main Component
const OfficeConsum = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchData, setSearchData] = useState({
    office_name: '',
    consumables_available: '',
    total_consumables_from: '',
    total_consumables_to: '',
    total_quantity_from: '',
    total_quantity_to: '',
    last_updated_from: null,
    last_updated_to: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);

  // Fetch office items data
  const { data: officeItems, isLoading, isSuccess, isError, error } = useGetOfficeItemsQuery('OfficeItemsList');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event, newValue, name) => {
    let updatedSearchData = { ...searchData };
    
    if (event?.target?.name) {
      updatedSearchData[event.target.name] = event.target.value;
    } else if (name) {
      // Handle date picker changes
      if (newValue === null) {
        updatedSearchData[name] = null;
      } else {
        updatedSearchData[name] = newValue;
      }
    }
    
    setSearchData(updatedSearchData);
    setPage(0);
  };

  const handleViewDetails = (office) => {
    setSelectedOffice(office);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOffice(null);
  };

  // Process and filter office items data
  const tabledata = useMemo(() => {
    if (!officeItems?.entities) return [];

    let data = Object.values(officeItems.entities);

    // Filter by office name
    if (searchData.office_name) {
      data = data.filter(office =>
        office.office_name?.toLowerCase().includes(searchData.office_name.toLowerCase())
      );
    }

    // Filter by consumables available
    if (searchData.consumables_available) {
      data = data.filter(office => {
        const breakdown = getConsumablesBreakdown(office.consumable_details);
        return breakdown.toLowerCase().includes(searchData.consumables_available.toLowerCase());
      });
    }

    // Filter by total consumables range
    if (searchData.total_consumables_from) {
      const fromValue = parseInt(searchData.total_consumables_from);
      if (!isNaN(fromValue)) {
        data = data.filter(office => {
          const totalConsumables = office.consumable_details?.length || 0;
          return totalConsumables >= fromValue;
        });
      }
    }
    if (searchData.total_consumables_to) {
      const toValue = parseInt(searchData.total_consumables_to);
      if (!isNaN(toValue)) {
        data = data.filter(office => {
          const totalConsumables = office.consumable_details?.length || 0;
          return totalConsumables <= toValue;
        });
      }
    }

    // Filter by total quantity range
    if (searchData.total_quantity_from) {
      const fromValue = parseInt(searchData.total_quantity_from);
      if (!isNaN(fromValue)) {
        data = data.filter(office => {
          const totalQuantity = office.consumable_details?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
          return totalQuantity >= fromValue;
        });
      }
    }
    if (searchData.total_quantity_to) {
      const toValue = parseInt(searchData.total_quantity_to);
      if (!isNaN(toValue)) {
        data = data.filter(office => {
          const totalQuantity = office.consumable_details?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
          return totalQuantity <= toValue;
        });
      }
    }

    // Filter by last updated date range
    if (searchData.last_updated_from) {
      const fromDate = dayjs(searchData.last_updated_from).startOf('day');
      data = data.filter(office => {
        if (!office.consumable_details || office.consumable_details.length === 0) return false;
        const lastUpdated = Math.max(...office.consumable_details.map(item => new Date(item.last_updated || 0).getTime()));
        return dayjs(lastUpdated).isSameOrAfter(fromDate);
      });
    }
    if (searchData.last_updated_to) {
      const toDate = dayjs(searchData.last_updated_to).endOf('day');
      data = data.filter(office => {
        if (!office.consumable_details || office.consumable_details.length === 0) return false;
        const lastUpdated = Math.max(...office.consumable_details.map(item => new Date(item.last_updated || 0).getTime()));
        return dayjs(lastUpdated).isSameOrBefore(toDate);
      });
    }

    // Sort by office name
    return data.sort((a, b) => 
      (a.office_name || '').localeCompare(b.office_name || '')
    );
  }, [officeItems, searchData]);

  // Prepare download data
  const downloadData = useMemo(() => {
    if (!tabledata || tabledata.length === 0) return [];

    return tabledata.map((office, index) => {
      const totalConsumables = office.consumable_details?.length || 0;
      const totalQuantity = office.consumable_details?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      const consumablesBreakdown = getConsumablesBreakdown(office.consumable_details);
      const lastUpdated = office.consumable_details?.length > 0
        ? moment(Math.max(...office.consumable_details.map(item => new Date(item.last_updated || 0).getTime()))).format('DD-MM-YYYY HH:mm')
        : '-';

      return {
        'Sl.No.': index + 1,
        'Office Name': office.office_name || '-',
        'Total Consumables': totalConsumables,
        'Total Quantity': totalQuantity,
        'Consumables Available': consumablesBreakdown,
        'Last Updated': lastUpdated,
      };
    });
  }, [tabledata]);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tabledata.length) : 0;

  if (isLoading) {
    return <PulseLoader color="#1976d2" />;
  }

  if (isError) {
    return <div>Error loading office consumables: {error?.data?.message || 'Unknown error'}</div>;
  }

  return (
    <>
      <div>
        <HeadingWrap>
          <Span>Office Consumables Inventory</Span>
        </HeadingWrap>

        <Button2Div>
          {downloadData && downloadData.length > 0 && (
            <ExcelDownload 
              csvData={downloadData} 
              fileName="Office_Consumables_Inventory"
            />
          )}
        </Button2Div>

        <TableContainer component={Paper} sx={TableContStyle}>
          <Table>
            <TableHead style={{ backgroundColor: TableHeadStyle.backgroundColor }}>
              <TableRow>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Sl.No.
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Office Name
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Total Consumables
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Total Quantity
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Consumables Available
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Last Updated
                </TableCell>
                <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>
                  Actions
                </TableCell>
              </TableRow>

              {/* Search Row */}
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: 'white' }}
                    name="office_name"
                    onChange={handleSearchChange}
                    label="Search Office"
                    size="small"
                    variant="outlined"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: 'white', width: '100%' }}
                    name="total_consumables_from"
                    onChange={handleSearchChange}
                    label="From"
                    size="small"
                    type="number"
                    variant="outlined"
                    fullWidth
                  />
                  {searchData?.total_consumables_from && (
                    <TextField
                      sx={{ background: 'white', width: '100%', marginTop: '8px' }}
                      name="total_consumables_to"
                      onChange={handleSearchChange}
                      label="To"
                      size="small"
                      type="number"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: 'white', width: '100%' }}
                    name="total_quantity_from"
                    onChange={handleSearchChange}
                    label="From"
                    size="small"
                    type="number"
                    variant="outlined"
                    fullWidth
                  />
                  {searchData?.total_quantity_from && (
                    <TextField
                      sx={{ background: 'white', width: '100%', marginTop: '8px' }}
                      name="total_quantity_to"
                      onChange={handleSearchChange}
                      label="To"
                      size="small"
                      type="number"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    sx={{ background: 'white' }}
                    name="consumables_available"
                    onChange={handleSearchChange}
                    label="Search Consumables"
                    size="small"
                    variant="outlined"
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DemoContainer components={['DatePicker', 'DatePicker']} sx={{ margin: '0px', display: 'flex' }}>
                      <Wrapper>
                        <DatePicker
                          label="From"
                          maxDate={searchData?.last_updated_to ? dayjs(searchData.last_updated_to) : null}
                          slotProps={{ textField: { size: 'small' } }}
                          sx={{ backgroundColor: 'white' }}
                          value={searchData?.last_updated_from ? dayjs(searchData.last_updated_from) : null}
                          format="DD/MM/YYYY"
                          onChange={(newValue) => handleSearchChange(null, newValue, 'last_updated_from')}
                        />
                        {searchData?.last_updated_from && (
                          <ClearIcon 
                            onClick={() => handleSearchChange(null, null, 'last_updated_from')}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                      </Wrapper>
                    </DemoContainer>
                  </LocalizationProvider>
                  {searchData?.last_updated_from && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={['DatePicker', 'DatePicker']} sx={{ margin: '0px', display: 'flex' }}>
                        <Wrapper>
                          <DatePicker
                            label="To"
                            minDate={searchData?.last_updated_from ? dayjs(searchData.last_updated_from) : null}
                            slotProps={{ textField: { size: 'small' } }}
                            sx={{ backgroundColor: 'white' }}
                            value={searchData?.last_updated_to ? dayjs(searchData.last_updated_to) : null}
                            format="DD/MM/YYYY"
                            onChange={(newValue) => handleSearchChange(null, newValue, 'last_updated_to')}
                          />
                          {searchData?.last_updated_to && (
                            <ClearIcon 
                              onClick={() => handleSearchChange(null, null, 'last_updated_to')}
                              sx={{ cursor: 'pointer' }}
                            />
                          )}
                        </Wrapper>
                      </DemoContainer>
                    </LocalizationProvider>
                  )}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? tabledata.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : tabledata
              ).map((office, index) => {
                const totalConsumables = office.consumable_details?.length || 0;
                const totalQuantity = office.consumable_details?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
                const consumablesBreakdown = getConsumablesBreakdown(office.consumable_details);
                const lastUpdated = office.consumable_details?.length > 0
                  ? moment(Math.max(...office.consumable_details.map(item => new Date(item.last_updated || 0).getTime()))).format('DD-MM-YYYY HH:mm')
                  : '-';

                return (
                  <TableRow key={office._id || index}>
                    <TableCell sx={{ fontSize: tableFontSize }}>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell sx={{ fontSize: tableFontSize }}>{office.office_name || '-'}</TableCell>
                    <TableCell sx={{ fontSize: tableFontSize }}>{totalConsumables}</TableCell>
                    <TableCell sx={{ fontSize: tableFontSize }}>{totalQuantity}</TableCell>
                    <TableCell sx={{ fontSize: tableFontSize }}>
                      <Tooltip title={consumablesBreakdown} arrow placement="top">
                        <ConsumablesBreakdownCell>
                          {consumablesBreakdown}
                        </ConsumablesBreakdownCell>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: tableFontSize }}>{lastUpdated}</TableCell>
                    <TableCell sx={{ fontSize: tableFontSize }}>
                      <IconButton
                        color="primary"
                        onClick={() => handleViewDetails(office)}
                        disabled={!office.consumable_details || office.consumable_details.length === 0}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}

              {tabledata.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No office consumables data found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={7}
                  count={tabledata.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>

      {/* Details Modal */}
      <ConsumableDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        officeData={selectedOffice}
      />
    </>
  );
};

export default OfficeConsum;